import React from 'react';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { Experiment, When, useExperiment } from '@carvana/core-experiment';
import { getUserState } from '@carvana/core-auth';
import { datadogRum } from '@datadog/browser-rum';
import { useSnapshot } from 'valtio';
import { getDaysSinceSale, isUserWithinXDays, SarStore } from '@store/sar-store';
import { Analytics } from '@services/analytics-service';
import { getHighIntentCookie } from '@utils/sarHighIntentCookie';
import { CUSTOMER_REGISTRATION_EXPERIMENT_NAME } from '../../types';
import { MFESkeletonLoader } from '../_common/skeleton-loader/SkeletonLoader';
import { Experiments, OvpFeedTypes } from '../../constants';
import ASRCard from '../asr-card/ASRCard';
import CarvanaCareCard from '../carvana-care-card/CarvanaCareCard';
import DigitalGloveBoxCard from '../digital-glovebox-card/DigitalGloveBoxCard';
import GapCoverageCard from '../gap-coverage-card/GapCoverageCard';
import LoanSummaryCard from '../loan-summary-card/LoanSummaryCard';
import RecallCard from '../recall-card/RecallCard';
import { RegistrationCard } from '../registration-card/RegistrationCard';
import SevenDayGuaranteeCard from '../seven-day-guarantee-card/SevenDayGuaranteeCard';
import STCOfferCard from '../stc-offer-card/STCOfferCard';
import STCPromoCard from '../stc-promo-card/STCPromoCard';
import WarrantiesCard from '../warranties-card/WarrantiesCard';
import { VehicleCoverage } from '../vehicle-coverage/vehicle-coverage';
import { OVPCardWrapper } from './OVPCard.styles';
import { ServiceAndRepairCard } from '../service-and-repair/service-and-repair-card';

type ovpCardProps = {
  feedCard: {};
  positionValue: number;
  userState: {};
  vehicleId: string;
};

const handleChatClick = () => {
  const chatBubble = document.querySelector('#chat-ui-bubble');
  // look for #chat-ui-bubble and click it
  if (chatBubble) {
    (chatBubble as HTMLElement).click();
  }
};

const OVPCard = ({ feedCard, positionValue, userState, vehicleId }: ovpCardProps) => {
  const { sarHighIntent } = useSnapshot(SarStore);
  const { assignedBucket } = useExperiment(Experiments.SEVEN_DAY_MONEY_BACK_GUARANTEE);
  const environment = process.env.NODE_ENV;
  const { type: feedCardType }: any = feedCard;
  let ovpCardType: any = null;

  const userId = getUserState()?.user?.user_id;

  // Return Card Type based on Feed Type value
  switch (feedCardType) {
    case OvpFeedTypes.REGISTRATION: {
      const regStatus = (feedCard as any)?.content?.registrationStatus;
      const chatBubble = document.querySelector('#chat-ui-bubble');

      ovpCardType = (
        <Experiment name={CUSTOMER_REGISTRATION_EXPERIMENT_NAME} identifier={userId}>
          <When bucket="treatment">
            <MfeReactComponent
              env={process.env.NODE_ENV}
              module="CustomerRegistrationMFE"
              component="default"
              loading={<MFESkeletonLoader />}
              vehicleId={vehicleId}
              onChat={chatBubble ? handleChatClick : undefined}
            />
          </When>
          <When default>
            <RegistrationCard registrationStatus={regStatus} vehicleId={vehicleId} />
          </When>
        </Experiment>
      );
      break;
    }

    case OvpFeedTypes.LOAN_N_PAYMENTS:
    case OvpFeedTypes.LOAN_PAID_OFF:
      ovpCardType = (
        <LoanSummaryCard
          environment={environment}
          feedData={feedCard}
          positionValue={positionValue}
          vehicleId={vehicleId}
        />
      );
      break;

    case OvpFeedTypes.SEVEN_DAY_GUARANTEE:
      ovpCardType = assignedBucket && assignedBucket !== 'treatment' && (
        <SevenDayGuaranteeCard feedData={feedCard} positionValue={positionValue} />
      );
      break;

    case OvpFeedTypes.DIGITAL_GLOVEBOX:
      ovpCardType = <DigitalGloveBoxCard feedData={feedCard} positionValue={positionValue} userState={userState} />;
      break;

    case OvpFeedTypes.CARVANA_CARE:
      ovpCardType = <CarvanaCareCard feedData={feedCard} positionValue={positionValue} />;
      break;

    case OvpFeedTypes.WARRANTY:
      ovpCardType = <WarrantiesCard feedData={feedCard} positionValue={positionValue} userState={userState} />;
      break;

    case OvpFeedTypes.VEHICLE_COVERAGE:
      ovpCardType = (
        <>
          <VehicleCoverage feedData={feedCard as any} positionValue={positionValue} userState={userState} />
        </>
      );
      break;

    case OvpFeedTypes.ACTIVE_TRADE:
    case OvpFeedTypes.EXPIRED_TRADE:
      ovpCardType = <STCOfferCard feedData={feedCard} positionValue={positionValue} />;
      break;

    case OvpFeedTypes.TRADE_PROMO:
      ovpCardType = <STCPromoCard feedData={feedCard} positionValue={positionValue} />;
      break;

    case OvpFeedTypes.GAP_COVERAGE:
      ovpCardType = <GapCoverageCard feedData={feedCard} positionValue={positionValue} />;
      break;

    case OvpFeedTypes.RECALLS:
      ovpCardType = <RecallCard feedData={feedCard} positionValue={positionValue} />;
      break;

    case OvpFeedTypes.AFTER_SALE_REPAIR:
      ovpCardType = <ASRCard feedData={feedCard} positionValue={positionValue} />;
      break;

    case OvpFeedTypes.SERVICE_AND_REPAIR: {
      const { highIntentCookie, hasHighIntentCookie, parsed } = getHighIntentCookie();
      const showSarsModule = sarHighIntent?.isHighIntent === true || !isUserWithinXDays(100);

      const props = {
        bucket:
          sarHighIntent?.experiments?.['inv-ltw-sr-intent'] !== undefined
            ? sarHighIntent?.experiments?.['inv-ltw-sr-intent']
            : null,
        isHighIntent: sarHighIntent?.isHighIntent !== undefined ? sarHighIntent?.isHighIntent : null,
        experiment: sarHighIntent?.experiments?.['inv-ltw-sr-intent'] !== undefined ? 'inv-ltw-sr-intent' : null,
        ...parsed,
        highIntentCookie,
        hasHighIntentCookie,
        vehicleId,
        showSarsModule,
        salesEffectiveDate: SarStore.saleEffectiveDate,
        daysSinceSale: SarStore.saleEffectiveDate ? getDaysSinceSale(SarStore.saleEffectiveDate) : null,
      };

      Analytics.trackEvent('ovp - service and repair - high intent', props);
      datadogRum.addAction('ovp - service and repair - high intent', props);

      if (!showSarsModule) {
        break;
      }

      ovpCardType = <ServiceAndRepairCard feedData={feedCard} positionValue={positionValue} />;
      break;
    }
    default:
      return null;
  }

  const ovpCardWrapper = ovpCardType && (
    <Experiment name={CUSTOMER_REGISTRATION_EXPERIMENT_NAME} identifier={userId}>
      <When bucket="treatment">
        <OVPCardWrapper id={feedCardType === OvpFeedTypes.REGISTRATION ? 'registration-card' : undefined} tabIndex={-1}>
          {ovpCardType}
        </OVPCardWrapper>
      </When>
      <When default>
        <OVPCardWrapper>{ovpCardType}</OVPCardWrapper>
      </When>
    </Experiment>
  );

  return ovpCardWrapper;
};

export default OVPCard;
